import React, {useCallback, useEffect, useState} from "react";
import AsyncSelect from "react-select/async";
import {apiV2} from "@/api/axiosConfig";
import {PaginatedResponse} from "@/types/PaginatedResponse";
import {reactSelectClassNames, reactSelectStyles} from "../react-select/styling";
import {noop} from "../misc/noop";

type Facility = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    address: {
        formatted: string;
    };
};

type Option = {
    id: string;
    label: string;
    facility: Facility;
};

type FacilitySelectV2Props = {
    rpp?: number;
    initialID?: string | null | undefined;
    clientID?: string;
    onChange?: (selected: Facility | undefined) => void;
    label?: string;
    required?: boolean;
};

export const FacilitySelectV2 = ({
    rpp = 20,
    initialID = null,
    clientID,
    onChange = noop,
    label,
    required = false,
}: FacilitySelectV2Props) => {
    const [selected, setSelected] = useState<Option | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!initialID) return;

        setIsLoading(true);
        apiV2.get<Facility>(`facilities/${initialID}`)
            .then((response) => {
                const facility = response.data;
                setSelected({
                    id: facility.id,
                    label: facility.name,
                    facility,
                });
            })
            .catch((error) => {
                console.error("Failed to load facility:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [initialID]);

    const onSelect = useCallback(
        (option: any) => {
            if (option) {
                setSelected(option);
                onChange(option.facility);
            } else {
                setSelected(null);
                onChange(undefined);
            }
        },
        [onChange]
    );

    const promiseOptions = useCallback(async (search: string) => {
        const response = await apiV2.get<PaginatedResponse<Facility>>("facilities", {
            params: {
                page: 1,
                limit: rpp,
                search: search || undefined,
                clientID: clientID || undefined,
            }
        });

        if (!response.data.items) return [];

        return response.data.items.map((f) => ({
            label: f.name,
            value: f.id,
            facility: f,
            id: f.id,
        }));
    }, [rpp, clientID]);

    if (isLoading) return null;

    return (
        <div className="flex flex-col relative">
            {label != undefined && (
                <label className="">
                    {label}
                    {required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <AsyncSelect
                classNames={reactSelectClassNames}
                styles={reactSelectStyles}
                placeholder="Select Facility"
                value={selected}
                onChange={onSelect}
                cacheOptions
                defaultOptions
                loadOptions={promiseOptions}
                isClearable
            />
        </div>
    );
};
